/**
 * Download flow for the ASK AI CSV download feature.
 *
 * One `idle` / `confirming` / `converting` state machine per answer. The
 * controls of an answer share it: `DownloadControl` reports an activation
 * through `requestDownload`, `AnswerTableDownloads` renders the confirmation
 * dialog from `phase` and `confirmRowCount`, the progress indicator from
 * `busyTableIndex`, and the error snackbar from `error`.
 *
 * The hook is the only place the conversion path is assembled — count rows,
 * extract the grid, serialize, name the file, hand it to the browser — and it
 * assembles that path out of the pure modules beside it. Two consequences
 * matter:
 *
 *   - **The answer is never touched.** Every step reads the DOM or a value
 *     derived from it; nothing writes to the table, the answer host, or the
 *     message list. So "the answer stays displayed" holds by construction
 *     through a successful download (Req 2.6), through a declined
 *     confirmation (Req 6.3), and through both error paths (Req 7.1, 7.2),
 *     rather than by careful state handling.
 *   - **Both failure paths land in one place.** `TableStructureError` and
 *     `DownloadFailedError` are mapped to their messages in one `catch`, and
 *     one `finally` returns the machine to idle, so no failure can leave a
 *     control stuck showing a progress indicator.
 *
 * The row threshold decides which of two conversion paths runs. Above it the
 * hook paints the busy state, waits one frame, then builds the grid in chunks
 * (Req 6.1, 6.2, 6.4). At or below it the whole conversion happens inside the
 * activation handler (Req 6.5) — a 1,000-row table is one DOM pass plus an
 * array join, measured at single-digit milliseconds, so there is nothing to
 * gain from making it asynchronous.
 */

import { useCallback, useRef, useState } from "react";

import { buildCsvFileName } from "./buildCsvFileName";
import {
  DOWNLOAD_FAILED_MESSAGE,
  DownloadDeps,
  DownloadFailedError,
  downloadCsvFile,
} from "./downloadCsvFile";
import { csvBlob, serializeCsv } from "./serializeCsv";
import {
  countDataRows,
  TableStructureError,
  tableToCellTextGrid,
  tableToCellTextGridChunked,
} from "./tableToCellTextGrid";
import {
  CellTextGrid,
  CHUNK_ROWS,
  DownloadPhase,
  DownloadState,
  LARGE_TABLE_ROW_THRESHOLD,
} from "./types";

// ── Messages ───────────────────────────────────────────────────────────────

/**
 * Reported when the table's cell structure could not be read (Req 7.1), and
 * when an activation failed for a reason this module does not recognise.
 */
export const CONVERSION_FAILED_MESSAGE =
  "That table could not be converted to CSV.";

/**
 * Text of the large-table confirmation prompt (Req 6.1).
 *
 * The count is interpolated as plain decimal digits rather than
 * locale-grouped, so the number the user is warned about appears in the prompt
 * verbatim.
 *
 * It lives here, next to the state that decides to prompt, rather than in the
 * component that renders the dialog, so that the obligation to report the data
 * row count sits with the code that knows the count.
 *
 * @param dataRowCount Data rows of the table about to be converted.
 */
export function largeTableConfirmMessage(dataRowCount: number): string {
  return (
    `This table has ${dataRowCount} data rows. ` +
    "Converting it to a CSV file may take a few seconds."
  );
}

// ── Public shape ───────────────────────────────────────────────────────────

/** The table one activation converts, and its position in the answer. */
export interface TableDownloadTarget {
  table: HTMLTableElement;
  /** Zero-based position index within the answer (Req 1.3). */
  tableIndex: number;
}

/**
 * Inputs the flow needs but does not own.
 *
 * `messageIndex` and `conversationId` are the two file-name parts the hook
 * cannot derive from the table (Req 4.1, 4.4); they arrive from the answer
 * being rendered. `now` and `downloadDeps` are seams: production callers omit
 * both and get the system clock and the real `URL`/`document`, while a test
 * passes a fixed instant to assert a deterministic file name and a capturing
 * stub to observe the emitted CSV or to drive the failure path of Req 7.2.
 */
export interface UseTableCsvDownloadOptions {
  /** Zero-based position of the answer in the conversation (Req 4.1). */
  messageIndex: number;
  /** Conversation identifier, absent while the conversation is unsaved. */
  conversationId?: string | null;
  /** Clock read once per download. Defaults to the system clock. */
  now?: () => Date;
  /** Download seam. Defaults to the real browser environment. */
  downloadDeps?: DownloadDeps;
}

/** State and commands the controls of one answer share. */
export interface UseTableCsvDownload {
  phase: DownloadPhase;
  /**
   * Index of the control whose table is being converted, or `null`. Drives the
   * progress indicator, so it is set only while `phase` is `converting`
   * (Req 6.4) — never while a confirmation is pending.
   */
  busyTableIndex: number | null;
  /** Data row count to report in the confirmation prompt (Req 6.1). */
  confirmRowCount: number | null;
  /** Message for the error snackbar, or `null` (Req 7.1, 7.2). */
  error: string | null;
  requestDownload: (target: TableDownloadTarget) => void;
  confirmDownload: () => void;
  cancelDownload: () => void;
  dismissError: () => void;
}

// ── Internals ──────────────────────────────────────────────────────────────

/**
 * The resting state, also the state every completed activation returns to.
 *
 * Shared across hook instances and frozen, because no transition mutates
 * state: each one builds a new object. Reusing this one identity also lets
 * React skip a re-render when an already-idle machine is reset.
 */
const IDLE_STATE: DownloadState = Object.freeze({
  phase: "idle",
  busyTableIndex: null,
  confirmRowCount: null,
  error: null,
});

/** Default clock, read at download time so the name stamps the download. */
function systemClock(): Date {
  return new Date();
}

/**
 * Resolves after the browser's next rendering opportunity, so a busy state set
 * just before this reaches the screen before the caller takes the thread back
 * (Req 6.4).
 *
 * `requestAnimationFrame` is the mechanism because its callback runs after a
 * paint, which is exactly the guarantee needed and is stronger than what a
 * task-scheduling timeout offers. The fallback covers a non-visual environment
 * that does not install the frame callback; yielding to the event loop is the
 * closest equivalent available there, and the conversion is correct either
 * way, since only the timing of the first paint differs.
 */
function nextAnimationFrame(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

/**
 * The user-visible message for a value thrown by an activation.
 *
 * The two in-scope failures each have their own message, stated
 * independently so that changing one cannot silently change the other. An
 * unrecognised value falls back to the conversion message, because whatever
 * went wrong, the table did not become a file.
 */
function errorMessageFor(thrown: unknown): string {
  if (thrown instanceof TableStructureError) {
    return CONVERSION_FAILED_MESSAGE;
  }
  if (thrown instanceof DownloadFailedError) {
    return DOWNLOAD_FAILED_MESSAGE;
  }
  return CONVERSION_FAILED_MESSAGE;
}

// ── Hook ───────────────────────────────────────────────────────────────────

/**
 * The download flow for the tables of one answer.
 *
 * ```text
 * idle       --activate, countDataRows >  5000--> confirming
 * idle       --activate, countDataRows <= 5000--> converting
 * confirming --confirm-----------------------------> converting
 * confirming --cancel-------------------------------> idle
 * converting --file handed off, or either error----> idle
 * ```
 *
 * `countDataRows` decides the branch and is called before either conversion
 * path starts. It reads row counts straight off the DOM without building a
 * grid, so asking the question costs nothing on the common path. The threshold
 * is strictly above (Req 6.1): a table with exactly 5,000 data rows converts
 * without a prompt.
 *
 * A declined confirmation is not an error (Req 6.3). It returns the machine to
 * idle with no message, and no conversion has run at that point — the grid is
 * built after the confirmation, never before it.
 *
 * One activation at a time. A second activation arriving while a confirmation
 * is pending or a conversion is running is dropped rather than queued, so a
 * double click cannot produce two files or leave two controls busy.
 */
export function useTableCsvDownload(
  options: UseTableCsvDownloadOptions
): UseTableCsvDownload {
  const {
    messageIndex,
    conversationId,
    now = systemClock,
    downloadDeps,
  } = options;

  const [state, setState] = useState<DownloadState>(IDLE_STATE);

  /**
   * True from the moment an activation is accepted until the flow returns to
   * idle. A ref rather than a read of `state.phase`, because the guard has to
   * hold against a second activation that arrives before React has re-rendered
   * with the first one's phase.
   */
  const inFlightRef = useRef(false);

  /**
   * The target waiting on a confirmation, held outside state because the
   * confirm handler needs the current value, not the value captured by the
   * render that opened the dialog. Cleared on every exit path.
   */
  const pendingRef = useRef<TableDownloadTarget | null>(null);

  /** Serialize a grid, name the file, and hand it to the browser (Req 2.5). */
  const emitCsvFile = useCallback(
    (grid: CellTextGrid, tableIndex: number): void => {
      const fileName = buildCsvFileName({
        conversationId,
        messageIndex,
        tableIndex,
        // Read at download time, so the stamp names the moment the user asked.
        now: now(),
      });
      downloadCsvFile(csvBlob(serializeCsv(grid)), fileName, downloadDeps);
    },
    [conversationId, messageIndex, now, downloadDeps]
  );

  /**
   * Return to idle, reporting `errorMessage` when the activation failed.
   *
   * Every exit path ends here — success, either error, and a declined
   * confirmation — which is what guarantees that `phase` and `busyTableIndex`
   * are always cleared (Req 7.1, 7.2).
   */
  const finishActivation = useCallback((errorMessage: string | null): void => {
    pendingRef.current = null;
    inFlightRef.current = false;
    setState(
      errorMessage === null
        ? IDLE_STATE
        : { ...IDLE_STATE, error: errorMessage }
    );
  }, []);

  /**
   * The synchronous path, for a table at or below the threshold (Req 6.5).
   *
   * Extract, serialize, name, download, all before this returns. The
   * `converting` phase set on the way in is never painted, because React
   * batches it with the reset in the `finally`; that is the point of taking
   * this path, and the phase is still set so the machine's history reads the
   * same on both paths.
   */
  const convertNow = useCallback(
    (target: TableDownloadTarget): void => {
      setState({
        phase: "converting",
        busyTableIndex: target.tableIndex,
        confirmRowCount: null,
        error: null,
      });

      let errorMessage: string | null = null;
      try {
        emitCsvFile(tableToCellTextGrid(target.table), target.tableIndex);
      } catch (thrown) {
        errorMessage = errorMessageFor(thrown);
      } finally {
        finishActivation(errorMessage);
      }
    },
    [emitCsvFile, finishActivation]
  );

  /**
   * The chunked path, for a table above the threshold (Req 6.2, 6.4).
   *
   * The frame wait between painting the busy state and starting the extraction
   * is load-bearing: without it the first chunk would run in the same task as
   * the state update, and the progress indicator the user is owed would not
   * reach the screen until the conversion had already begun.
   */
  const convertInChunks = useCallback(
    async (target: TableDownloadTarget): Promise<void> => {
      setState({
        phase: "converting",
        busyTableIndex: target.tableIndex,
        confirmRowCount: null,
        error: null,
      });

      let errorMessage: string | null = null;
      try {
        await nextAnimationFrame();
        const grid = await tableToCellTextGridChunked(target.table, {
          chunkRows: CHUNK_ROWS,
        });
        emitCsvFile(grid, target.tableIndex);
      } catch (thrown) {
        errorMessage = errorMessageFor(thrown);
      } finally {
        finishActivation(errorMessage);
      }
    },
    [emitCsvFile, finishActivation]
  );

  /**
   * Handle one control activation: prompt above the threshold, convert at or
   * below it (Req 6.1).
   */
  const requestDownload = useCallback(
    (target: TableDownloadTarget): void => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;

      const dataRowCount = countDataRows(target.table);
      if (dataRowCount > LARGE_TABLE_ROW_THRESHOLD) {
        pendingRef.current = target;
        setState({
          phase: "confirming",
          // No progress indicator yet: nothing is being converted while the
          // prompt is up (Req 6.4).
          busyTableIndex: null,
          confirmRowCount: dataRowCount,
          // A retry clears the message left by an earlier failure.
          error: null,
        });
        return;
      }

      convertNow(target);
    },
    [convertNow]
  );

  /** Accept the confirmation prompt and convert the table (Req 6.2). */
  const confirmDownload = useCallback((): void => {
    const target = pendingRef.current;
    if (target === null) {
      return;
    }
    pendingRef.current = null;
    void convertInChunks(target);
  }, [convertInChunks]);

  /**
   * Decline the confirmation prompt (Req 6.3).
   *
   * No conversion has run, so there is nothing to undo and nothing to report:
   * the machine returns to idle with no message. Ignored when no prompt is
   * pending, so a stray call cannot clear an error the user has not read.
   */
  const cancelDownload = useCallback((): void => {
    if (pendingRef.current === null) {
      return;
    }
    finishActivation(null);
  }, [finishActivation]);

  /** Dismiss the error snackbar, leaving the current phase alone. */
  const dismissError = useCallback((): void => {
    setState((previous) =>
      previous.error === null ? previous : { ...previous, error: null }
    );
  }, []);

  return {
    phase: state.phase,
    busyTableIndex: state.busyTableIndex,
    confirmRowCount: state.confirmRowCount,
    error: state.error,
    requestDownload,
    confirmDownload,
    cancelDownload,
    dismissError,
  };
}
